import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';
import { newBlockId } from '../blockId';

/**
 * Document-level housekeeping of the block editor (#526):
 *
 *  - TrailingNode: the document always ends with an empty text block, so after an
 *    atom block (image, quiz, hotspot …) there is somewhere to click, type and
 *    open the "/" menu. Without it the lesson was stuck once an image was the
 *    last block.
 *  - UniqueBlockIds: Enter splits a text block and ProseMirror copies the attrs,
 *    so both halves carried the same `blockId`; duplicate ids break grading and
 *    version diffs. Every top-level block without an id, or with an id already
 *    used above it, gets a fresh one.
 */
export const TrailingNode = Extension.create({
    name: 'oaTrailingNode',

    addProseMirrorPlugins() {
        const key = new PluginKey('oaTrailingNode');
        return [
            new Plugin({
                key,
                appendTransaction: (_transactions, _oldState, newState) => {
                    const { doc, schema } = newState;
                    const last = doc.lastChild;
                    const type = schema.nodes.richtextBlock;
                    if (!type || (last && last.type === type)) return null;
                    return newState.tr.insert(doc.content.size, type.create({ blockId: newBlockId(), blockMeta: null, html: '' }));
                },
            }),
        ];
    },
});

export const UniqueBlockIds = Extension.create({
    name: 'oaUniqueBlockIds',

    addProseMirrorPlugins() {
        const key = new PluginKey('oaUniqueBlockIds');
        return [
            new Plugin({
                key,
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((t) => t.docChanged)) return null;
                    const seen = new Set<string>();
                    let tr: Transaction | null = null;
                    newState.doc.forEach((node, offset) => {
                        if (!('blockId' in node.attrs)) return;
                        const id = node.attrs.blockId as string | null;
                        if (id && !seen.has(id)) {
                            seen.add(id);
                            return;
                        }
                        const fresh = newBlockId();
                        seen.add(fresh);
                        tr = (tr ?? newState.tr).setNodeMarkup(offset, undefined, { ...node.attrs, blockId: fresh });
                    });
                    return tr;
                },
            }),
        ];
    },
});
